<template>
  <div class="kt-autoselect" ref="autoselectRef">
    <button 
      type="button"
      class="autoselect-trigger"
      @click="toggleDropdown"
    >
      <span class="trigger-label">{{ props.label }}</span>
      <span v-if="props.multiple && selectedCount > 0" class="badge bg-primary ms-2">
        {{ selectedCount }}
      </span>
      <i class="fas fa-chevron-down ms-1"></i>
    </button>
    
    <div v-if="dropdownVisible" class="autoselect-dropdown dropdown-menu show" @click.stop>
          <!-- Header Row -->
          <div class="dropdown-header-row d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
            <span class="fw-semibold">{{ props.label }}</span>
            <div class="d-flex gap-2">
              <button 
                v-if="props.onRefresh !== null"
                type="button"
                class="btn btn-link btn-sm text-decoration-none p-0"
                @click="handleRefresh"
                title="Refresh data"
              >
                <i class="fas fa-sync"></i>
              </button>
              <button 
                v-if="selectedCount > 0"
                type="button"
                class="btn btn-link btn-sm text-decoration-none p-0"
                @click="clearSelection"
                title="Clear selection"
              >
                Clear
              </button>
            </div>
          </div>
          
          <!-- Search Row -->
          <div class="dropdown-search-row px-3 py-2 border-bottom">
            <div class="input-group input-group-sm">
              <span class="input-group-text">
                <i class="fas fa-search"></i>
              </span>
              <input
                ref="searchInputRef"
                type="text"
                class="form-control"
                :placeholder="props.searchPlaceholder || 'Search...'"
                v-model="searchValue"
                @input="handleSearch"
                @keydown="handleKeydown"
              />
              <button 
                v-if="searchValue"
                type="button"
                class="btn btn-outline-secondary"
                @click="clearSearch"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
          
          <!-- Options List -->
          <div class="dropdown-options" :style="optionsStyle">
            <div v-if="isLoading" class="dropdown-item text-center text-muted">
              <i class="fas fa-spinner fa-spin me-2"></i>
              Loading...
            </div>
            
            <div v-else-if="filteredOptions.length === 0" class="dropdown-item text-muted">
              {{ searchValue ? 'No results found' : 'No options available' }}
            </div>
            
            <div v-else>
              <div
                v-for="(option, index) in filteredOptions"
                :key="getOptionKey(option, index)"
                class="dropdown-item autoselect-option"
                :class="{ 
                  'active': index === highlightedIndex,
                  'selected': isOptionSelected(option)
                }"
                @click="selectOption(option)"
                @mouseenter="highlightedIndex = index"
              >
                <div class="d-flex align-items-center">
                  <i 
                    v-if="props.multiple" 
                    :class="[
                      'me-2',
                      isOptionSelected(option) ? 'fas fa-check-square text-primary' : 'far fa-square text-muted'
                    ]"
                  ></i>
                  <span>{{ getOptionDisplay(option) }}</span>
                </div>
              </div>
            </div>
          </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted, type PropType, getCurrentInstance } from 'vue';

interface AutoselectOption {
  [key: string]: any;
}

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  options: {
    type: Array as () => AutoselectOption[],
    default: () => []
  },
  searchPlaceholder: {
    type: String,
    default: 'Search...'
  },
  displayBy: {
    type: [String, Function] as PropType<string | ((item: any) => string)>,
    default: 'name'
  },
  valueBy: {
    type: [String, Function] as PropType<string | ((item: any) => any)>,
    default: 'id'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  returnObject: {
    type: Boolean,
    default: false
  },
  maxHeight: {
    type: String,
    default: '300px'
  },
  minLength: {
    type: Number,
    default: 0
  },
  debounceMs: {
    type: Number,
    default: 300
  },
  serverSide: {
    type: Boolean,
    default: false
  },
  onRefresh: {
    type: Function as PropType<() => Promise<void> | void>,
    default: null
  }
});

const emit = defineEmits<{
  change: [value: any];
  update: [filter: { search: string }];
}>();

const model = defineModel<any>({
  default: () => []
});

// State
const autoselectRef = ref<HTMLElement | null>(null);
const searchInputRef = ref<HTMLInputElement | null>(null);
const dropdownVisible = ref(false);
const searchValue = ref('');
const highlightedIndex = ref(-1);
const selectedOptions = ref<AutoselectOption[]>([]);
const isLoading = ref(false);
const searchTimeout = ref<NodeJS.Timeout | null>(null);
const debouncedSearch = ref('');

// Check if parent is listening to update event
const hasUpdateListener = computed(() => {
  const instance = getCurrentInstance();
  return !!(instance?.vnode.props?.onUpdate);
});

// Computed
const selectedCount = computed(() => {
  if (props.multiple) {
    return Array.isArray(model.value) ? model.value.length : 0;
  }
  return model.value ? 1 : 0;
});

const filteredOptions = computed(() => {
  if (!props.options || !Array.isArray(props.options)) {
    return [];
  }
  
  // If we have an update listener or serverSide is true, assume data is already filtered by server
  if (hasUpdateListener.value || props.serverSide) {
    return props.options;
  }
  
  // Client-side filtering
  if (!debouncedSearch.value || debouncedSearch.value.length < props.minLength) {
    return props.options;
  }
  
  const searchTerm = debouncedSearch.value.toLowerCase().trim();
  if (!searchTerm) {
    return props.options;
  }
  
  return props.options.filter(option => {
    const displayValue = getOptionDisplay(option).toLowerCase();
    return displayValue.includes(searchTerm);
  });
});

const optionsStyle = computed(() => ({
  maxHeight: props.maxHeight,
  overflowY: 'auto' as const
}));

// Helper functions
const getOptionDisplay = (option: AutoselectOption): string => {
  if (!option) return '';
  
  const displayBy = props.displayBy;
  
  if (typeof displayBy === 'function') {
    return String(displayBy(option));
  }
  
  if (typeof displayBy === 'string' && option[displayBy] !== undefined) {
    return String(option[displayBy]);
  }
  
  // Fallback
  if (option.name !== undefined) return String(option.name);
  if (option.id !== undefined) return String(option.id);
  
  return String(option);
};

const getOptionKey = (option: AutoselectOption, index: number): string => {
  const valueBy = props.valueBy;
  
  if (typeof valueBy === 'function') {
    return String(valueBy(option));
  }
  
  if (typeof valueBy === 'string' && option[valueBy] !== undefined) {
    return String(option[valueBy]);
  }
  
  return `option-${index}`;
};

const getOptionValue = (option: AutoselectOption): any => {
  if (!option) return '';
  
  if (props.returnObject) {
    return option;
  }
  
  const valueBy = props.valueBy;
  
  if (typeof valueBy === 'function') {
    return valueBy(option);
  }
  
  if (typeof valueBy === 'string' && option[valueBy] !== undefined) {
    return option[valueBy];
  }
  
  return option;
};

const isOptionSelected = (option: AutoselectOption): boolean => {
  if (props.multiple) {
    const modelArray = Array.isArray(model.value) ? model.value : [];
    const optionValue = getOptionValue(option);
    return modelArray.some(val => {
      if (props.returnObject) {
        return JSON.stringify(val) === JSON.stringify(optionValue);
      }
      return val === optionValue;
    });
  }
  
  const optionValue = getOptionValue(option);
  if (props.returnObject) {
    return JSON.stringify(model.value) === JSON.stringify(optionValue);
  }
  return model.value === optionValue;
};

// Event handlers
const toggleDropdown = () => {
  dropdownVisible.value = !dropdownVisible.value;
  
  if (dropdownVisible.value) {
    // Focus search input when dropdown opens
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  }
};

const handleSearch = () => {
  highlightedIndex.value = -1;
  
  // Handle server-side filtering with debouncing
  if (hasUpdateListener.value || props.serverSide) {
    handleServerSideSearch();
  } else {
    // Client-side filtering with debouncing
    handleClientSideSearch();
  }
};

const handleServerSideSearch = () => {
  clearSearchTimeout();
  
  searchTimeout.value = setTimeout(() => {
    if (searchValue.value.length >= props.minLength) {
      isLoading.value = true;
      emit('update', {
        search: searchValue.value
      });
    }
  }, props.debounceMs);
};

const handleClientSideSearch = () => {
  clearSearchTimeout();
  
  searchTimeout.value = setTimeout(() => {
    debouncedSearch.value = searchValue.value;
  }, props.debounceMs);
};

const clearSearchTimeout = () => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
    searchTimeout.value = null;
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault();
      highlightedIndex.value = Math.min(highlightedIndex.value + 1, filteredOptions.value.length - 1);
      break;
      
    case 'ArrowUp':
      event.preventDefault();
      highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1);
      break;
      
    case 'Enter':
      event.preventDefault();
      if (highlightedIndex.value >= 0 && filteredOptions.value[highlightedIndex.value]) {
        selectOption(filteredOptions.value[highlightedIndex.value]);
      }
      break;
      
    case 'Escape':
      event.preventDefault();
      dropdownVisible.value = false;
      highlightedIndex.value = -1;
      break;
  }
};

const selectOption = (option: AutoselectOption) => {
  if (props.multiple) {
    const currentArray = Array.isArray(model.value) ? [...model.value] : [];
    const optionValue = getOptionValue(option);
    
    // Check if already selected
    const existingIndex = currentArray.findIndex(val => {
      if (props.returnObject) {
        return JSON.stringify(val) === JSON.stringify(optionValue);
      }
      return val === optionValue;
    });
    
    if (existingIndex >= 0) {
      // Remove if already selected
      currentArray.splice(existingIndex, 1);
      selectedOptions.value = selectedOptions.value.filter(opt => 
        getOptionValue(opt) !== optionValue
      );
    } else {
      // Add if not selected
      currentArray.push(optionValue);
      selectedOptions.value.push(option);
    }
    
    model.value = currentArray;
    // Keep dropdown open for multiple selection
  } else {
    model.value = getOptionValue(option);
    selectedOptions.value = [option];
    dropdownVisible.value = false;
  }
  
  highlightedIndex.value = -1;
  emit('change', model.value);
  
  // Return focus to search input for multiple mode
  if (props.multiple) {
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  }
};

const clearSelection = () => {
  if (props.multiple) {
    model.value = [];
    selectedOptions.value = [];
  } else {
    model.value = '';
    selectedOptions.value = [];
  }
  emit('change', model.value);
  
  // Return focus to search input
  nextTick(() => {
    searchInputRef.value?.focus();
  });
};

const clearSearch = () => {
  searchValue.value = '';
  debouncedSearch.value = '';
  highlightedIndex.value = -1;
  
  nextTick(() => {
    searchInputRef.value?.focus();
  });
};

const handleRefresh = async () => {
  if (props.onRefresh) {
    isLoading.value = true;
    try {
      await props.onRefresh();
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      isLoading.value = false;
    }
  }
};

// Find option by model value
const findOptionByValue = (value: any): AutoselectOption | null => {
  if (!value || !props.options) return null;
  
  return props.options.find(option => {
    const optionValue = getOptionValue(option);
    if (props.returnObject) {
      return JSON.stringify(optionValue) === JSON.stringify(value);
    }
    return optionValue == value;
  }) || null;
};

const findOptionsByValues = (values: any[]): AutoselectOption[] => {
  if (!Array.isArray(values) || !props.options) return [];
  
  return values.map(value => findOptionByValue(value)).filter(Boolean) as AutoselectOption[];
};

// Watchers
watch(() => model.value, (newValue) => {
  if (props.multiple) {
    const values = Array.isArray(newValue) ? newValue : [];
    selectedOptions.value = findOptionsByValues(values);
  } else {
    const option = findOptionByValue(newValue);
    selectedOptions.value = option ? [option] : [];
  }
}, { immediate: true });

watch(() => props.options, () => {
  // Re-evaluate the selected options when options change
  if (props.multiple) {
    const values = Array.isArray(model.value) ? model.value : [];
    selectedOptions.value = findOptionsByValues(values);
  } else if (model.value) {
    const option = findOptionByValue(model.value);
    selectedOptions.value = option ? [option] : [];
  }
  
  // Clear loading state
  isLoading.value = false;
});

watch(dropdownVisible, (visible) => {
  if (visible) {
    // Focus search input when dropdown opens
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  } else {
    // Clear search when dropdown closes
    searchValue.value = '';
    debouncedSearch.value = '';
    highlightedIndex.value = -1;
    clearSearchTimeout();
  }
});

// Click outside detection
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  
  if (
    dropdownVisible.value &&
    autoselectRef.value &&
    !autoselectRef.value.contains(target)
  ) {
    dropdownVisible.value = false;
  }
};

// Setup and cleanup
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  clearSearchTimeout();
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.kt-autoselect {
  display: inline-block;
  position: relative;
}

.autoselect-trigger {
  background: transparent;
  border: 1px solid transparent;
  color: #444;
  cursor: pointer;
  text-decoration: none;
  padding: 0.375rem 0.75rem;
  display: inline-flex;
  align-items: center;
  font-size: 0.97rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.autoselect-trigger:hover {
  color: #0a58ca;
  background-color: #f8f9fa;
  border-color: #dee2e6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.autoselect-trigger:active,
.autoselect-trigger:focus {
  background-color: #e9ecef;
  border-color: #ced4da;
  outline: none;
}

.autoselect-trigger .trigger-label {
  font-weight: 300;
}

.autoselect-trigger .badge {
  font-size: 0.75rem;
  padding: 0.25em 0.5em;
}

.autoselect-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1050;
  min-width: 300px;
  max-width: 400px;
  margin-top: 0.125rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

.dropdown-header-row {
  background-color: #f8f9fa;
}

.dropdown-search-row {
  background-color: #ffffff;
}

.dropdown-options {
  overflow-y: auto;
}

.autoselect-option {
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: background-color 0.15s ease-in-out;
}

.autoselect-option:hover,
.autoselect-option.active {
  background-color: #e9ecef;
  color: #212529;
}

.autoselect-option.selected {
  background-color: #e7f3ff;
  color: #0d6efd;
  font-weight: 500;
}

.autoselect-option i {
  font-size: 1rem;
}

</style>
